import { ContentType } from 'src/app/models/documents/folder.model';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveItem, MoveCallbacks } from './drive-viewer.models';
import { DriveMoveState, DriveWorkspaceState } from './drive-viewer.state';

export class DriveMoveExecutionCoordinator {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly state: DriveMoveState,
    private readonly selection: DriveSelectionQuery,
    private readonly presentation: DriveItemPresentationPolicy,
    private readonly documents: DocumentService,
    private readonly logger: FrontendLoggerService,
    private readonly translate: (key: string) => string,
    private readonly refresh: (silent: boolean) => void
  ) {}

  moveFiles(
    files: DriveItem[],
    folder: DriveItem,
    silentRefresh = false,
    confirmMove = false,
    callbacks?: MoveCallbacks
  ): boolean {
    const targetId = Number.parseInt(folder?.id, 10);
    if (!folder || folder.type !== 'folder' || this.selection.isVirtualFolderId(folder.id) || !Number.isFinite(targetId)) {
      return false;
    }
    const moving = files.filter((file) => this.selection.canMoveFileToFolder(file, folder.id)
      && !this.state.pendingMoveKeys.has(this.moveKey(file.id, folder.id)));
    if (!moving.length || (confirmMove && !this.confirmDropMove(moving, folder))) {
      return false;
    }
    this.executeBatch(moving, folder, targetId, silentRefresh, callbacks);
    return true;
  }

  resolveDocumentId(item: DriveItem | undefined): string | null {
    const raw = item?.documentId ?? item?.id;
    const value = raw == null ? '' : String(raw).trim();
    return value || null;
  }

  confirmDropMove(items: DriveItem[], destination: DriveItem): boolean {
    const itemLabel = items.length === 1
      ? `"${items[0].name}"`
      : `${items.length} ${this.translate('drive.items') || 'elementos'}`;
    const message = this.translate('drive.confirm_drop_move') || `¿Mover ${itemLabel} a "${destination.name}"?`;
    try { return globalThis.confirm(message); } catch { return true; }
  }

  moveKey(fileId: string, folderId: string): string { return `${fileId}:${folderId}`; }

  requestDocumentId(documentId: string | undefined): string | null {
    const value = (documentId || '').trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
      ? value
      : null;
  }

  selectedFiles(): DriveItem[] { return this.selection.selectedFiles(); }
  fileTypeLabel(item: DriveItem): string { return this.selection.fileTypeLabel(item); }
  extension(item: DriveItem): string | undefined { return this.presentation.extension(item) || undefined; }

  extractCreatedFolderId(created: any): number | null {
    const candidates = [
      created?.id, created?.contentId, created?.folderId, created?.data?.id,
      created?.data?.contentId, created?.data?.folderId, created?.content?.id, created?.result?.id
    ];
    for (const candidate of candidates) {
      const value = typeof candidate === 'string'
        ? Number.parseInt(candidate, 10)
        : typeof candidate === 'number' ? candidate : Number.NaN;
      if (Number.isFinite(value) && value > 0) return value;
    }
    return null;
  }

  private executeBatch(
    files: DriveItem[],
    folder: DriveItem,
    targetId: number,
    silentRefresh: boolean,
    callbacks?: MoveCallbacks
  ): void {
    let completed = 0;
    let failed = 0;
    const finish = (success: boolean) => {
      completed++;
      if (!success) failed++;
      if (completed !== files.length) return;
      if (failed === 0) {
        this.refresh(silentRefresh);
        callbacks?.success?.();
      } else {
        callbacks?.error?.();
      }
    };
    files.forEach((file) => this.moveOne(file, folder, targetId, finish));
  }

  private moveOne(
    file: DriveItem,
    folder: DriveItem,
    targetId: number,
    finish: (success: boolean) => void
  ): void {
    const contentId = Number.parseInt(file.id, 10);
    const pendingKey = this.moveKey(file.id, folder.id);
    const payload = {
      id: contentId,
      name: file.name,
      contentType: ContentType.FILE,
      documentId: this.requestDocumentId(file.documentId),
      parentId: targetId,
      fileSizeBytes: file.size || null
    };
    this.state.pendingMoveKeys.add(pendingKey);
    this.logger.info(`[DriveViewerComponent] Calling updateDocumentFolder for move: ${JSON.stringify({ contentId, targetId })}`);
    this.documents.updateDocumentFolder(contentId, payload).subscribe({
      next: () => {
        const index = this.workspace.items.findIndex((item) => item.id === file.id);
        if (index >= 0) {
          const destination = this.workspace.items.find((item) => item.id === folder.id && item.type === 'folder');
          this.workspace.items[index] = {
            ...this.workspace.items[index],
            parentId: folder.id,
            path: [...(destination?.path || [])]
          };
        }
      },
      error: (error) => {
        this.state.pendingMoveKeys.delete(pendingKey);
        this.logger.error(`[DriveViewerComponent] Error moviendo documento: ${JSON.stringify({ file, error })}`);
        finish(false);
      },
      complete: () => {
        this.state.pendingMoveKeys.delete(pendingKey);
        finish(true);
      }
    });
  }
}
